import { fetchWithAuth } from '../utils/fetchWithAuth';

/**
 * Parse error response from FastAPI.
 * @param {Object} error - Error object from response
 * @param {string} defaultMessage - Default error message
 * @returns {string} Parsed error message
 */
function parseErrorResponse(error, defaultMessage) {
  if (!error.detail) {
    return defaultMessage;
  }
  
  if (Array.isArray(error.detail)) {
    return error.detail.map(e => {
      if (typeof e === 'string') return e;
      if (e.msg) return e.msg;
      if (e.loc) return `${e.loc.join('.')}: ${e.msg || JSON.stringify(e)}`;
      return JSON.stringify(e);
    }).join(', ');
  }
  
  if (typeof error.detail === 'string') {
    return error.detail;
  }
  
  return JSON.stringify(error.detail);
}

/**
 * Fetch all individual clients from the backend.
 * @returns {Promise<Array>} List of individual clients.
 */
export async function getIndividualClients() {
  try {
    const response = await fetchWithAuth('/api/clients/individual-clients');
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, 'Failed to fetch individual clients'));
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching individual clients:', error);
    throw error;
  }
}

/**
 * Fetch all entity clients from the backend.
 * @returns {Promise<Array>} List of entity clients.
 */
export async function getEntityClients() {
  try {
    const response = await fetchWithAuth('/api/clients/entity-clients');
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, 'Failed to fetch entity clients'));
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching entity clients:', error);
    throw error;
  }
}

/**
 * Post a new individual client to the backend.
 * @param {Object} data - The client data (name, id_number, email, phone, address, tax_number, date_of_birth)
 * @returns {Promise<Object>} The response from the backend
 */
export async function postIndividualClient(data) {
  try {
    const response = await fetchWithAuth("/api/clients/individual", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, "Failed to add individual client"));
    }
    return response.json();
  } catch (error) {
    console.error('Error posting individual client:', error);
    throw error;
  }
}

/**
 * Post a new entity client to the backend.
 * @param {Object} data - The entity client data (name, legal_name, registration_number, license_number, email, phone, address)
 * @returns {Promise<Object>} The response from the backend
 */
export async function postEntityClient(data) {
  try {
    const response = await fetchWithAuth("/api/clients/entity", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, "Failed to add entity client"));
    }
    return response.json();
  } catch (error) {
    console.error('Error posting entity client:', error);
    throw error;
  }
}

/**
 * Post multiple individual clients to the backend in bulk.
 * @param {Array} clients - Array of individual client data
 * @returns {Promise<Object>} The response from the backend
 */
export async function postBulkIndividualClients(clients) {
  const response = await fetchWithAuth("/api/clients/individual/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clients }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const errorMessage = parseErrorResponse(error, "Failed to add individual clients");
    const errorObj = new Error(errorMessage);
    errorObj.detail = error.detail;
    throw errorObj;
  }
  return response.json();
}

/**
 * Post multiple entity clients to the backend in bulk.
 * @param {Array} clients - Array of entity client data
 * @returns {Promise<Object>} The response from the backend
 */
export async function postBulkEntityClients(clients) {
  const response = await fetchWithAuth("/api/clients/entity/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clients }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const errorMessage = parseErrorResponse(error, "Failed to add entity clients");
    const errorObj = new Error(errorMessage);
    errorObj.detail = error.detail;
    throw errorObj;
  }
  return response.json();
}

/**
 * Fetch a single individual client by ID.
 * @param {string} clientId - The client ID to fetch
 * @returns {Promise<Object>} The individual client data
 */
export async function getIndividualClient(clientId) {
  try {
    const response = await fetchWithAuth(`/api/clients/individual/${clientId}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, 'Failed to fetch individual client'));
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching individual client:', error);
    throw error;
  }
}

/**
 * Fetch a single entity client by ID.
 * @param {string} clientId - The client ID to fetch
 * @returns {Promise<Object>} The entity client data
 */
export async function getEntityClient(clientId) {
  try {
    const response = await fetchWithAuth(`/api/clients/entity/${clientId}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, 'Failed to fetch entity client'));
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching entity client:', error);
    throw error;
  }
}

/**
 * Update an existing individual client.
 * @param {string} clientId - The client ID to update
 * @param {Object} data - The client data to update (name, id_number, email, phone, address, tax_number, date_of_birth)
 * @returns {Promise<Object>} The updated client data from the backend
 */
export async function updateIndividualClient(clientId, data) {
  try {
    const response = await fetchWithAuth(`/api/clients/individual/${clientId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, "Failed to update individual client"));
    }
    return response.json();
  } catch (error) {
    console.error('Error updating individual client:', error);
    throw error;
  }
}

/**
 * Update an existing entity client.
 * @param {string} clientId - The client ID to update
 * @param {Object} data - The entity client data to update (name, legal_name, registration_number, license_number, email, phone, address)
 * @returns {Promise<Object>} The updated client data from the backend
 */
export async function updateEntityClient(clientId, data) {
  try {
    const response = await fetchWithAuth(`/api/clients/entity/${clientId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, "Failed to update entity client"));
    }
    return response.json();
  } catch (error) {
    console.error('Error updating entity client:', error);
    throw error;
  }
}

/**
 * Fetch the list of users from the backend using the get_users_list RPC.
 * @returns {Promise<{users: Array}>} The list of users in an object with a 'users' property.
 */
export async function getUsers() {
  try {
    const response = await fetchWithAuth("/api/users");
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(parseErrorResponse(error, "Failed to fetch users"));
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
} 